import { Order } from '@features/siteStatusV2/types';
import theme from '@theme';
import React, { Dispatch, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import styled from 'styled-components';

import { Icon } from '../icon';
import OrderElement from './OrderElement';
import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';

interface OrderListProps {
  orderSelected: Order | undefined;
  setOrderSelected: Dispatch<React.SetStateAction<Order | undefined>>;
}

const OrderList: React.FC<OrderListProps> = ({ orderSelected, setOrderSelected }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const { isOrderLoading, orderList } = useSelector((state: RootState) => state.orderList);

  useEffect(() => {
    if (isOrderLoading) {
      setOrderSelected(undefined);
    }
  }, [isOrderLoading]);

  if (isOrderLoading) {
    return (
      <Row>
        <Col className="col-md-6">
          {Array.from({ length: 5 }).map((_, index) => (
            <SkeletonContainer key={index} className="mb-2">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center justify-content-between">
                  <Skeleton
                    count={1}
                    width={78}
                    height={16}
                    baseColor={theme.palette.grey[200]}
                    borderRadius={4}
                  />
                  <Skeleton
                    count={1}
                    width={4}
                    height={4}
                    baseColor={theme.palette.grey[300]}
                    borderRadius={100}
                    className="mx-2"
                    style={{ position: 'relative', top: '-2px' }}
                  />
                  <Skeleton
                    count={1}
                    width={138}
                    height={8}
                    baseColor={theme.palette.grey[200]}
                    borderRadius={5}
                    className="pl-4"
                  />
                </div>
                <Skeleton
                  count={1}
                  width={35}
                  height={16}
                  baseColor={theme.palette.grey[200]}
                  borderRadius={4}
                />
              </div>
              <Skeleton
                count={1}
                width={108}
                height={8}
                baseColor={theme.palette.grey[200]}
                borderRadius={9}
                className="mt-2"
              />
              <SkeletonAction>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton
                    key={index}
                    count={1}
                    width={3}
                    height={3}
                    baseColor={theme.palette.grey[300]}
                    borderRadius={100}
                  />
                ))}
              </SkeletonAction>
            </SkeletonContainer>
          ))}
        </Col>
      </Row>
    );
  }

  if (!orderList) {
    return null;
  }

  if (orderList.length === 0 && !isOrderLoading) {
    return (
      <div className="text-center mt-5">
        <Icon iconName="empty-data" />
        <NoOrder>{t('OrderList.noOrder', `No order`)}</NoOrder>
      </div>
    );
  }

  return (
    <Col className="col-md-6">
      {orderList.map((order) => (
        <OrderElement
          order={order}
          orderSelected={orderSelected}
          key={order.id}
          setOrderSelected={setOrderSelected}
        />
      ))}
    </Col>
  );
};

const SkeletonContainer = styled.div`
  position: relative;
  width: 100%;
  border: 1px solid ${theme.palette.grey[200]};
  border-radius: 0.25rem;
  padding: 0.4rem 2.75rem 0.4rem 1.125rem;
`;

const SkeletonAction = styled.div`
  position: absolute;
  top: 0.7rem;
  right: 1.125rem;
  display: flex;
  flex-direction: column;
  margin-left: 0.7rem;

  > span {
    width: 3px;
    height: 3px;
    margin-top: 2px;
    position: relative;
    top: -15px;
  }
`;

const NoOrder = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${theme.palette.grey[400]};
`;

export default OrderList;
